import dayjs from 'dayjs';

import {
  RELIABILITY_METRICS,
  ReliabilityMetricKey,
} from 'features/cluster/_components/reliability/_constants/reliabilityMetrics';
import { ReliabilityMetricsData } from 'features/cluster/_components/reliability/_utils/buildReliabilityChartData';

export type ReliabilityDailyTableRow = Partial<
  Record<ReliabilityMetricKey, number>
> & {
  date: string;
  availabilityPercent?: number;
  errorRatePercent?: number;
};

type DailyMetricAggregate = {
  sum: number;
  count: number;
};

type AggregationByMetric = Partial<
  Record<ReliabilityMetricKey, DailyMetricAggregate>
>;

export const buildReliabilityDailyTableData = (
  data: ReliabilityMetricsData | undefined,
  timezone: string,
  maxRows = 30
): ReliabilityDailyTableRow[] => {
  const rowsByDate = new Map<string, AggregationByMetric>();

  RELIABILITY_METRICS.forEach(({ key }) => {
    data?.[key]?.values?.forEach(({ timestamp, value }) => {
      if (!timestamp || value === undefined) {
        return;
      }

      const dateKey = dayjs.utc(timestamp).tz(timezone).format('YYYY-MM-DD');
      const dateMetrics = rowsByDate.get(dateKey) ?? {};
      const metricAggregate = dateMetrics[key] ?? { sum: 0, count: 0 };

      dateMetrics[key] = {
        sum: metricAggregate.sum + value,
        count: metricAggregate.count + 1,
      };

      rowsByDate.set(dateKey, dateMetrics);
    });
  });

  const buildRowFromMetrics = (
    date: string,
    metrics: AggregationByMetric
  ): ReliabilityDailyTableRow => {
    const row: ReliabilityDailyTableRow = { date };

    RELIABILITY_METRICS.forEach(({ key }) => {
      const aggregate = metrics[key];

      if (!aggregate?.count) {
        return;
      }

      row[key] = aggregate.sum / aggregate.count;
    });

    const requestRate = row.requestRate;
    const errorRate = row.errorRate;

    if (
      requestRate !== undefined &&
      requestRate > 0 &&
      errorRate !== undefined
    ) {
      row.errorRatePercent = (errorRate / requestRate) * 100;
      row.availabilityPercent = 100 - row.errorRatePercent;
    }

    return row;
  };

  return [...rowsByDate.entries()]
    .map(([date, metrics]) => buildRowFromMetrics(date, metrics))
    .sort((a, b) => b.date.localeCompare(a.date))
    .slice(0, maxRows);
};
